<template>
  <div class="settings-page">
    <router-link class="breadcrumb" to="/admin-pages">
      <span class="breadcrumb-icon">←</span>
      {{ $adminT('adminAll.back_to_operations_console_6f9440', 'Back to Operations Console') }}
    </router-link>

    <div class="page-header">
      <div>
        <p class="eyebrow">{{ $adminT('adminAll.commercial_settings_c62d8d', 'COMMERCIAL SETTINGS') }}</p>
        <h1>{{ $adminT('adminAll.organization_settings_514d77', 'Organization Settings') }}</h1>
        <p class="lede">
          {{ $adminT('adminAll.manage_organizer_fee_kickback_contract_expiry_and_ticket_s_6377b3', 'Manage organizer fee, kickback, contract expiry, and ticket seller fee settings. These settings are restricted to super admins.') }}
        </p>
      </div>
      <div class="summary">
        <span class="summary-count">{{ organizations.length + ticketSellerOrganizations.length || '—' }}</span>
        <span class="summary-label">{{ $adminT('adminAll.total_5c6505', 'TOTAL') }}</span>
      </div>
    </div>

    <div class="card">
      <p class="section-title">{{ $adminT('adminAll.organizer_entities_13da25', 'Organizer entities') }}</p>
      <div v-if="loading" class="settings-skeleton" aria-hidden="true">
        <div class="settings-skeleton__field">
          <span class="settings-skeleton__label" />
          <span class="settings-skeleton__input" />
        </div>
        <div class="settings-skeleton__grid">
          <span v-for="index in 4" :key="`organization-field-${index}`" class="settings-skeleton__block" />
        </div>
      </div>
      <div v-else class="field">
        <label>{{ $adminT('adminAll.organization_519255', 'Organization') }}</label>
        <v-select
          v-model="selectedOrganization"
          :options="organizations"
          :filterable="true"
          label="legalName"
          :placeholder="$adminT('adminAll.search_by_legal_name_public_name_slug_or_oib_f500a8', 'Search by legal name, public name, slug, or OIB')"
        >
          <template #option="{ legalName, publicName, slug, OIB }">
            <div class="organization-option">
              <strong>{{ legalName }}</strong>
              <span>{{ publicName || '-' }} · {{ slug || '-' }} {{ $adminT('adminAll.oib_b6362d', '· OIB:') }} {{ OIB || '-' }}</span>
            </div>
          </template>
          <template #selected-option="{ legalName, publicName, slug, OIB }">
            <div class="organization-option">
              <strong>{{ legalName }}</strong>
              <span>{{ publicName || '-' }} · {{ slug || '-' }} {{ $adminT('adminAll.oib_b6362d', '· OIB:') }} {{ OIB || '-' }}</span>
            </div>
          </template>
        <template slot="no-options">{{ $adminT('adminAll.sorry_no_matching_options_f0c837', 'Sorry, no matching options.') }}</template></v-select>
      </div>

      <div v-if="selectedOrganization" class="settings-grid">
        <div class="field">
          <label>{{ $adminT('adminAll.service_fee_d27d6d', 'Service fee (%)') }}</label>
          <input v-model.number="form.serviceFee" type="number" step="0.01" min="0">
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.coreevent_fee_per_ticket_eur_59596d', 'CoreEvent fee per ticket (EUR)') }}</label>
          <input v-model.number="form.fixedFeePerTicket" type="number" step="0.01" min="0">
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.coreevent_variable_fee_2c51a6', 'CoreEvent variable fee (%)') }}</label>
          <input v-model.number="form.variableFeePercentage" type="number" step="0.01" min="0">
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.kickback_per_ticket_eur_725c4a', 'Kickback per ticket (EUR)') }}</label>
          <input v-model.number="form.kickbackPerTicket" type="number" step="0.01" min="0">
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.contract_expiry_date_a3deea', 'Contract expiry date') }}</label>
          <input v-model="form.contractExpiresAt" type="date">
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.kulturna_iskaznica_redni_broj_upisnika_cedd7f', 'Kulturna iskaznica — redni broj upisnika') }}</label>
          <input
            v-model="form.kiRedniBrojUpisnika"
            type="text"
            :placeholder="$adminT('adminAll.e_g_ki102_24f244', 'e.g. KI102')"
          >
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.kulturna_iskaznica_vrsta_troska_ulaznice_e0a57d', 'Kulturna iskaznica — vrsta troška (ulaznice)') }}</label>
          <select v-model.number="form.kiSifraVrsteTroska">
            <option :value="null">{{ $adminT('adminAll.not_set_93039e', 'Not set') }}</option>
            <option v-for="costType in kiTicketCostTypes" :key="costType.code" :value="costType.code">
              {{ costType.code }} — {{ costType.label }}
            </option>
          </select>
        </div>
      </div>

      <p v-if="selectedOrganization" class="helper-text">
        {{ $adminT('adminAll.kulturna_iskaznica_is_offered_at_checkout_only_when_both_k_c4389b', 'Kulturna iskaznica is offered at checkout only when both KI fields are set. The redni broj upisnika is the organizer\'s own entry in the Upisnik pružatelja kulturnih sadržaja — they must be registered there and connected to us as their posrednik. Their tickets are booked under it, so an incorrect value is rejected by Fina.') }}
      </p>

      <div v-if="selectedOrganization" class="details-grid">
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.legal_name_15cd2b', 'Legal name') }}</p>
          <p class="detail-value">{{ selectedOrganization.legalName || '—' }}</p>
        </div>
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.primary_email_b008d1', 'Primary email') }}</p>
          <p class="detail-value">{{ selectedOrganization.primaryEmail || selectedOrganization.email || '—' }}</p>
        </div>
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.slug_oib_86fc96', 'Slug / OIB') }}</p>
          <p class="detail-value">{{ selectedOrganization.slug || '—' }} · {{ selectedOrganization.OIB || '—' }}</p>
        </div>
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.address_d70f93', 'Address') }}</p>
          <p class="detail-value">{{ formatAddress(selectedOrganization) }}</p>
        </div>
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.3_month_reminder_sent_4fef69', '3 month reminder sent') }}</p>
          <p class="detail-value">{{ formatDateTime(selectedOrganization.contractExpiryReminder3mSentAt) }}</p>
        </div>
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.1_month_reminder_sent_fdf450', '1 month reminder sent') }}</p>
          <p class="detail-value">{{ formatDateTime(selectedOrganization.contractExpiryReminder1mSentAt) }}</p>
        </div>
      </div>

      <p v-if="selectedOrganization" class="helper-text">
        {{ $adminT('adminAll.contract_expiry_drives_internal_reminder_emails_changing_t_f7db9f', 'Contract expiry drives internal reminder emails. Changing the date resets reminder state so the new schedule can be sent correctly.') }}
      </p>

      <div class="actions">
        <main-button class="" flat :disabled="!selectedOrganization || saving" @click.native="resetForm">
          {{ $adminT('adminAll.reset_44c57a', 'Reset') }}
        </main-button>
        <main-button
          class=""
          depressed
          :disabled="!selectedOrganization || saving"
          :loading="saving"
          @click.native="saveSettings"
        >
          {{ $adminT('adminAll.save_organizer_settings_38dd27', 'Save organizer settings') }}
        </main-button>
      </div>
      <div v-if="error" class="alert error">{{ adminAllMessage(error) }}</div>
      <div v-if="success" class="alert success">{{ adminAllMessage(success) }}</div>
    </div>

    <div class="card">
      <p class="section-title">{{ $adminT('adminAll.ticket_seller_entities_9dbceb', 'Ticket seller entities') }}</p>
      <div v-if="ticketSellersLoading" class="settings-skeleton" aria-hidden="true">
        <div class="settings-skeleton__field">
          <span class="settings-skeleton__label" />
          <span class="settings-skeleton__input" />
        </div>
        <div class="settings-skeleton__grid settings-skeleton__grid--short">
          <span
            v-for="index in 3"
            :key="`ticket-seller-field-${index}`"
            class="settings-skeleton__block"
          />
        </div>
      </div>
      <div v-else class="field">
        <label>{{ $adminT('adminAll.ticket_seller_organization_2ac4e5', 'Ticket seller organization') }}</label>
        <v-select
          v-model="selectedTicketSellerOrganization"
          :options="ticketSellerOrganizations"
          :filterable="true"
          label="name"
          :placeholder="$adminT('adminAll.search_by_name_or_oib_cd4c51', 'Search by name or OIB')"
        >
          <template #option="{ name, oib, id }">
            <div class="organization-option">
              <strong>{{ name }}</strong>
              <span>{{ $adminT('adminAll.id_d789a1', 'ID:') }} {{ id }} {{ $adminT('adminAll.oib_b6362d', '· OIB:') }} {{ oib || '-' }}</span>
            </div>
          </template>
          <template #selected-option="{ name, oib, id }">
            <div class="organization-option">
              <strong>{{ name }}</strong>
              <span>{{ $adminT('adminAll.id_d789a1', 'ID:') }} {{ id }} {{ $adminT('adminAll.oib_b6362d', '· OIB:') }} {{ oib || '-' }}</span>
            </div>
          </template>
        <template slot="no-options">{{ $adminT('adminAll.sorry_no_matching_options_f0c837', 'Sorry, no matching options.') }}</template></v-select>
      </div>

      <div v-if="selectedTicketSellerOrganization" class="settings-grid">
        <div class="field">
          <label>{{ $adminT('adminAll.service_fee_d27d6d', 'Service fee (%)') }}</label>
          <input v-model.number="ticketSellerForm.ticketPercentageFee" type="number" step="0.01" min="0">
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.buyer_fee_per_ticket_eur_04b746', 'Buyer fee per ticket (EUR)') }}</label>
          <input v-model.number="ticketSellerForm.ticketAdditionalFeeAmount" type="number" step="0.01" min="0">
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.coreevent_share_of_buyer_fee_bf6537', 'CoreEvent share of buyer fee (%)') }}</label>
          <input
            v-model.number="ticketSellerForm.coreEventAdditionalFeePercentage"
            type="number"
            step="0.01"
            min="0"
            max="100"
          >
        </div>
        <div class="field">
          <label>{{ $adminT('adminAll.kickback_per_ticket_eur_725c4a', 'Kickback per ticket (EUR)') }}</label>
          <input v-model.number="ticketSellerForm.kickbackPerTicket" type="number" step="0.01" min="0">
        </div>
        <div class="field checkbox-field">
          <label>
            <input v-model="ticketSellerForm.useEventFeePerTicket" type="checkbox">
            {{ $adminT('adminAll.apply_coreevent_event_fee_per_sold_ticket_fc272b', 'Apply CoreEvent event fee per sold ticket') }}
          </label>
        </div>
      </div>

      <div v-if="selectedTicketSellerOrganization" class="details-grid">
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.ticket_seller_name_f070d4', 'Ticket seller name') }}</p>
          <p class="detail-value">{{ selectedTicketSellerOrganization.name || '—' }}</p>
        </div>
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.oib_linked_organizer_93d2dc', 'OIB / linked organizer') }}</p>
          <p class="detail-value">
            {{ selectedTicketSellerOrganization.oib || '—' }} ·
            {{ selectedTicketSellerOrganization.organizationLegalName || $adminT('adminAll.standalone_1d1032', 'Standalone') }}
          </p>
        </div>
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.seller_type_a2c14b', 'Seller type') }}</p>
          <p class="detail-value">{{ adminAllLabel(selectedTicketSellerOrganization.type) || '—' }}</p>
        </div>
        <div class="detail-card">
          <p class="detail-label">{{ $adminT('adminAll.address_d70f93', 'Address') }}</p>
          <p class="detail-value">{{ selectedTicketSellerOrganization.address || '—' }}</p>
        </div>
      </div>

      <p v-if="selectedTicketSellerOrganization" class="helper-text">
        {{ $adminT('adminAll.these_values_are_stored_on_the_ticket_seller_fee_config_an_644fac', 'These values are stored on the ticket seller fee config and affect ticket seller payout calculations only.') }}
      </p>

      <div class="actions">
        <main-button
          class=""
          flat
          :disabled="!selectedTicketSellerOrganization || ticketSellerSaving"
          @click.native="resetTicketSellerForm"
        >
          {{ $adminT('adminAll.reset_44c57a', 'Reset') }}
        </main-button>
        <main-button
          class=""
          depressed
          :disabled="!selectedTicketSellerOrganization || ticketSellerSaving"
          :loading="ticketSellerSaving"
          @click.native="saveTicketSellerSettings"
        >
          {{ $adminT('adminAll.save_ticket_seller_settings_47571b', 'Save ticket seller settings') }}
        </main-button>
      </div>
      <div v-if="ticketSellersError" class="alert error">{{ adminAllMessage(ticketSellersError) }}</div>
      <div v-if="ticketSellersSuccess" class="alert success">{{ adminAllMessage(ticketSellersSuccess) }}</div>
    </div>
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import vSelect from './AdminSelect.vue'
import 'vue-select/dist/vue-select.css'

import {
  getAdminOrganizations,
  getTicketSellerOrganizations,
  updateAdminOrganizationSettings,
  updateTicketSellerOrganizationSettings,
} from '@/http'

export default {
  mixins: [AdminAllMessages],
  provide: { adminLocalization: true },
  name: 'organization-settings',
  components: {
    'v-select': vSelect,
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      success: null,
      organizations: [],
      kiTicketCostTypes: [],
      selectedOrganization: null,
      ticketSellersLoading: false,
      ticketSellerSaving: false,
      ticketSellersError: null,
      ticketSellersSuccess: null,
      ticketSellerOrganizations: [],
      selectedTicketSellerOrganization: null,
      form: {
        serviceFee: 0,
        fixedFeePerTicket: 0,
        variableFeePercentage: 0,
        kickbackPerTicket: 0,
        contractExpiresAt: '',
        kiRedniBrojUpisnika: '',
        kiSifraVrsteTroska: null,
      },
      ticketSellerForm: {
        ticketPercentageFee: 0,
        ticketAdditionalFeeAmount: 0,
        coreEventAdditionalFeePercentage: 0,
        kickbackPerTicket: 0,
        useEventFeePerTicket: false,
      },
    }
  },
  watch: {
    selectedOrganization: {
      immediate: false,
      handler(organization) {
        if (!organization) {
          return
        }
        this.form = this.buildForm(organization)
        this.error = null
        this.success = null
      },
    },
    selectedTicketSellerOrganization: {
      immediate: false,
      handler(ticketSellerOrganization) {
        if (!ticketSellerOrganization) {
          return
        }
        this.ticketSellerForm = {
          ticketPercentageFee: Number(ticketSellerOrganization.ticketPercentageFee || 0),
          ticketAdditionalFeeAmount: Number(ticketSellerOrganization.ticketAdditionalFeeAmount || 0),
          coreEventAdditionalFeePercentage: Number(ticketSellerOrganization.coreEventAdditionalFeePercentage || 0),
          kickbackPerTicket: Number(ticketSellerOrganization.kickbackPerTicket || 0),
          useEventFeePerTicket: Boolean(ticketSellerOrganization.useEventFeePerTicket),
        }
        this.ticketSellersError = null
        this.ticketSellersSuccess = null
      },
    },
  },
  created() {
    this.loadOrganizations()
    this.loadTicketSellerOrganizations()
  },
  methods: {
    buildForm(organization) {
      return {
        serviceFee: Number(organization.serviceFee || 0),
        fixedFeePerTicket: Number(organization.fixedFeePerTicket || 0),
        variableFeePercentage: Number(organization.variableFeePercentage || 0),
        kickbackPerTicket: Number(organization.kickbackPerTicket || 0),
        contractExpiresAt: organization.contractExpiresAt || '',
        kiRedniBrojUpisnika: organization.kiRedniBrojUpisnika || '',
        kiSifraVrsteTroska: organization.kiSifraVrsteTroska || null,
      }
    },
    async loadOrganizations() {
      this.loading = true
      this.error = null
      try {
        const { data } = await getAdminOrganizations()
        this.organizations = (data && data.organizations) || []
        this.kiTicketCostTypes = (data && data.kiTicketCostTypes) || []
      } catch (err) {
        this.error =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to load organizations.')
      } finally {
        this.loading = false
      }
    },
    async loadTicketSellerOrganizations() {
      this.ticketSellersLoading = true
      this.ticketSellersError = null
      try {
        const { data } = await getTicketSellerOrganizations(true)
        this.ticketSellerOrganizations = (data && data.ticketSellerOrganizations) || []
      } catch (err) {
        this.ticketSellersError =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to load ticket sellers.')
      } finally {
        this.ticketSellersLoading = false
      }
    },
    resetForm() {
      if (!this.selectedOrganization) {
        return
      }
      this.form = this.buildForm(this.selectedOrganization)
      this.error = null
      this.success = null
    },
    async saveSettings() {
      if (!this.selectedOrganization) {
        return
      }
      this.error = null
      this.success = null

      if (Number(this.form.kickbackPerTicket || 0) > Number(this.form.fixedFeePerTicket || 0)) {
        this.error = this.adminAllNotice('Kickback per ticket cannot exceed CoreEvent fee per ticket.')

        return
      }

      // Half-configured KI silently leaves the payment method hidden, which is
      // hard to tell apart from a bug. Make the incomplete pair an explicit error.
      if (Boolean(this.form.kiRedniBrojUpisnika) !== Boolean(this.form.kiSifraVrsteTroska)) {
        this.error =
          this.adminAllNotice('Set both Kulturna iskaznica fields, or clear both. Kulturna iskaznica stays disabled until both are set.')

        return
      }

      this.saving = true
      try {
        const { data } = await updateAdminOrganizationSettings(this.selectedOrganization.id, {
          serviceFee: Number(this.form.serviceFee || 0),
          fixedFeePerTicket: Number(this.form.fixedFeePerTicket || 0),
          variableFeePercentage: Number(this.form.variableFeePercentage || 0),
          kickbackPerTicket: Number(this.form.kickbackPerTicket || 0),
          contractExpiresAt: this.form.contractExpiresAt || null,
          kiRedniBrojUpisnika: this.form.kiRedniBrojUpisnika || null,
          kiSifraVrsteTroska: this.form.kiSifraVrsteTroska || null,
        })

        const updatedOrganization = (data && data.organization) || null
        if (updatedOrganization) {
          this.organizations = this.organizations.map(organization =>
            organization.id === updatedOrganization.id ? updatedOrganization : organization,
          )
          this.selectedOrganization = updatedOrganization
        }

        this.success = this.adminAllNotice('Organization settings saved.')
      } catch (err) {
        this.error =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to save organization settings.')
      } finally {
        this.saving = false
      }
    },
    resetTicketSellerForm() {
      if (!this.selectedTicketSellerOrganization) {
        return
      }
      this.ticketSellerForm = {
        ticketPercentageFee: Number(this.selectedTicketSellerOrganization.ticketPercentageFee || 0),
        ticketAdditionalFeeAmount: Number(this.selectedTicketSellerOrganization.ticketAdditionalFeeAmount || 0),
        coreEventAdditionalFeePercentage: Number(
          this.selectedTicketSellerOrganization.coreEventAdditionalFeePercentage || 0,
        ),
        kickbackPerTicket: Number(this.selectedTicketSellerOrganization.kickbackPerTicket || 0),
        useEventFeePerTicket: Boolean(this.selectedTicketSellerOrganization.useEventFeePerTicket),
      }
      this.ticketSellersError = null
      this.ticketSellersSuccess = null
    },
    async saveTicketSellerSettings() {
      if (!this.selectedTicketSellerOrganization) {
        return
      }

      this.ticketSellersError = null
      this.ticketSellersSuccess = null

      if (Number(this.ticketSellerForm.ticketPercentageFee || 0) < 0) {
        this.ticketSellersError = this.adminAllNotice('Service fee percentage cannot be negative.')

        return
      }
      if (Number(this.ticketSellerForm.ticketAdditionalFeeAmount || 0) < 0) {
        this.ticketSellersError = this.adminAllNotice('Buyer fee per ticket cannot be negative.')

        return
      }
      if (Number(this.ticketSellerForm.kickbackPerTicket || 0) < 0) {
        this.ticketSellersError = this.adminAllNotice('Kickback per ticket cannot be negative.')

        return
      }
      if (
        Number(this.ticketSellerForm.coreEventAdditionalFeePercentage || 0) < 0 ||
        Number(this.ticketSellerForm.coreEventAdditionalFeePercentage || 0) > 100
      ) {
        this.ticketSellersError = this.adminAllNotice('CoreEvent share percentage must be between 0 and 100.')

        return
      }

      this.ticketSellerSaving = true
      try {
        const { data } = await updateTicketSellerOrganizationSettings(this.selectedTicketSellerOrganization.id, {
          ticketPercentageFee: Number(this.ticketSellerForm.ticketPercentageFee || 0),
          ticketAdditionalFeeAmount: Number(this.ticketSellerForm.ticketAdditionalFeeAmount || 0),
          coreEventAdditionalFeePercentage: Number(this.ticketSellerForm.coreEventAdditionalFeePercentage || 0),
          kickbackPerTicket: Number(this.ticketSellerForm.kickbackPerTicket || 0),
          useEventFeePerTicket: Boolean(this.ticketSellerForm.useEventFeePerTicket),
        })

        const updatedTicketSellerOrganization = (data && data.ticketSellerOrganization) || null
        if (updatedTicketSellerOrganization) {
          this.ticketSellerOrganizations = this.ticketSellerOrganizations.map(ticketSellerOrganization =>
            ticketSellerOrganization.id === updatedTicketSellerOrganization.id
              ? updatedTicketSellerOrganization
              : ticketSellerOrganization,
          )
          this.selectedTicketSellerOrganization = updatedTicketSellerOrganization
        }

        this.ticketSellersSuccess = this.adminAllNotice('Ticket seller settings saved.')
      } catch (err) {
        this.ticketSellersError =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          this.adminAllNotice('Failed to save ticket seller settings.')
      } finally {
        this.ticketSellerSaving = false
      }
    },
    formatDateTime(value) {
      if (!value) {
        return this.$adminT('adminAll.not_sent_587c50', 'Not sent')
      }
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) {
        return String(value)
      }

      return new Intl.DateTimeFormat('hr-HR', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(parsed)
    },
    formatAddress(entity) {
      const parts = [entity.address, entity.postalCode, entity.city].filter(Boolean)

      return parts.length ? parts.join(', ') : '—'
    },
  },
}
</script>

<style scoped>
.settings-page {
  padding: 2rem;
}

.breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #3d5b9a;
  text-decoration: none;
  margin-bottom: 1.5rem;
}

.breadcrumb-icon {
  font-size: 1rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.eyebrow {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  color: #7f8da3;
}

.lede {
  margin: 0;
  color: #6b7280;
  max-width: 40rem;
}

.summary {
  min-width: 5rem;
  padding: 1rem;
  border-radius: 1rem;
  background: #f8fafc;
  text-align: center;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.summary-count {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
}

.summary-label {
  display: block;
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  color: #7f8da3;
}

.card {
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #ffffff;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.06);
}

.section-title {
  margin: 0 0 1rem;
  font-size: 1rem;
  font-weight: 600;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
}

.field input,
.field select {
  width: 100%;
  min-height: 2.75rem;
  padding: 0.75rem 0.875rem;
  border: 1px solid #dbe3ef;
  border-radius: 0.75rem;
  color: #1f2937;
  background-color: #fff;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.detail-card {
  padding: 0.875rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #f8fafc;
}

.detail-label {
  margin: 0 0 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.detail-value {
  margin: 0;
  color: #0f172a;
  word-break: break-word;
}

.checkbox-field {
  justify-content: flex-end;
}

.checkbox-field label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.organization-option {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.organization-option span {
  font-size: 0.8rem;
  color: #6b7280;
  white-space: normal;
  word-break: break-word;
}

.helper-text {
  margin: 1rem 0 0;
  color: #6b7280;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.settings-skeleton {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-skeleton__field,
.settings-skeleton__grid {
  display: grid;
  gap: 0.75rem;
}

.settings-skeleton__grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.settings-skeleton__grid--short {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.settings-skeleton__label,
.settings-skeleton__input,
.settings-skeleton__block {
  display: block;
  border-radius: 0.75rem;
  background: linear-gradient(90deg, #eef2f7 25%, #e2e8f0 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: settings-skeleton-shimmer 1.4s ease-in-out infinite;
}

.settings-skeleton__label {
  width: 8rem;
  height: 0.875rem;
}

.settings-skeleton__input {
  width: 100%;
  height: 2.75rem;
}

.settings-skeleton__block {
  width: 100%;
  min-height: 5.25rem;
}

.alert {
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
}

.alert.error {
  background: #fef2f2;
  color: #b91c1c;
}

.alert.success {
  background: #ecfdf5;
  color: #047857;
}

.card ::v-deep .vs__dropdown-toggle {
  min-height: 2.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid #dbe3ef;
}

.card ::v-deep .vs__selected-options,
.card ::v-deep .vs__selected,
.card ::v-deep .vs__dropdown-option {
  white-space: normal;
}

.card ::v-deep .vs__dropdown-option {
  word-break: break-word;
}

.card ::v-deep .vs__dropdown-menu {
  border-radius: 0.75rem;
  border: 1px solid #dbe3ef;
}

@keyframes settings-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

@media (max-width: 1200px) {
  .settings-grid,
  .details-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .settings-page {
    padding: 1.25rem;
  }

  .card {
    padding: 1.25rem;
  }

  .settings-grid {
    grid-template-columns: 1fr;
  }

  .details-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .page-header {
    flex-direction: column;
  }

  .summary {
    align-self: flex-start;
  }
}

@media (max-width: 640px) {
  .settings-page {
    padding: 0.875rem;
  }

  .card {
    padding: 1rem;
  }

  .details-grid,
  .settings-skeleton__grid,
  .settings-skeleton__grid--short {
    grid-template-columns: 1fr;
  }

  .actions {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
